import { ThemeType } from 'types/publishedAssets';
import { readableTextColor } from 'utils/readableTextColor';
import { accessibleBrandColor, WCAG_AAA_CONTRAST } from 'utils/accessibleBrandColor';
import { INTER_FALLBACK_FONT_FACE, THEME_FONT_FALLBACK_STACK } from '@/constants/fonts';
import { DEFAULT_HEADLINE_COLOR } from '@/constants/brandColors';

// Theme values land in a raw <style> block, so strip the characters that could
// close the element (<, >) or the quoted font-family strings (', ") — whatever
// the published brand data holds, the worst it can produce is an odd-looking
// value, never markup.
const cssSafe = (value: string) => value.replace(/[<>'"]/g, '');

// The surface --theme-header-footer-on-white-color is measured against: the white
// pill that ActionButton's .home variant paints. Change one and change the other.
const WHITE_PILL_SURFACE = '#fff';

/**
 * Builds the brand stylesheet: the metric-adjusted Inter fallback face plus the
 * --theme-* custom properties the whole app reads.
 */
export function buildThemeCss(theme?: ThemeType): string {
  const actionColor = cssSafe(theme?.whitelabelColor || '#0f2563');
  const headerFooterColor = cssSafe(theme?.whitelabelBackgroundColor || '#000');
  const linkColor = cssSafe(theme?.whitelabelLinkColor || actionColor);
  // Brand accent for text on a white surface — today the update page's back-to-project
  // pill. The nav color is the brand's own answer for that accent, but a white or pale
  // nav vanishes against the pill, so the link color follows it as a candidate and a
  // shade of whichever carries the brand hue is used when neither clears the ratio.
  //
  // The bar is AAA rather than AA: the pill is small, low-emphasis navigation text, and
  // holding it to 7:1 keeps it readable at a glance without hunting for it.
  const headerFooterOnWhiteColor = accessibleBrandColor(
    [headerFooterColor, linkColor],
    WHITE_PILL_SURFACE,
    WCAG_AAA_CONTRAST,
  );

  // These token names are also consumed outside this repo: the consentmanager CMP's
  // custom CSS reads them by name to theme the consent banner, which renders in a
  // shadow root that page CSS cannot reach. Renaming one silently falls back to the
  // CMP's own defaults with nothing here to catch it — see docs/consent-manager.md.
  //
  // The doubled :root outranks the single-:root defaults in
  // @zencity/common-survey/lib/style.css (pulled in by src/styles/globals.css),
  // which the document head emits after the tags collected from next/head.
  return `${INTER_FALLBACK_FONT_FACE}
    :root:root {
    --theme-progress-bar-color: ${cssSafe(theme?.whitelabelColor || '#24d394')};
    --theme-header-footer-color: ${headerFooterColor};
    --theme-header-footer-text-color: ${readableTextColor(headerFooterColor)};
    --theme-header-footer-on-white-color: ${headerFooterOnWhiteColor};
    --theme-action-color: ${actionColor};
    --theme-action-text-color: ${readableTextColor(actionColor)};
    --theme-action-hover-color: ${cssSafe(theme?.whitelabelColor || '#0b1c4a')};
    --theme-action-focus-color: ${cssSafe(theme?.whitelabelColor || '#3a53c6')};
    --theme-link-color: ${linkColor};
    --theme-headline-color: ${cssSafe(theme?.whitelabelTextColor || DEFAULT_HEADLINE_COLOR)};
    --theme-headline-font: '${cssSafe(theme?.headingFont || 'Inter')}', ${THEME_FONT_FALLBACK_STACK};
    --theme-paragraph-font: '${cssSafe(theme?.paragraphFont || theme?.sansSerifFont || 'Inter')}', ${THEME_FONT_FALLBACK_STACK};
    }`;
}

/**
 * The Google Fonts families this theme needs, deduplicated — heading and
 * paragraph commonly resolve to the same family, and one request covers both.
 */
export function customFontFamilies(theme?: ThemeType): string[] {
  const families = [theme?.headingFont, theme?.paragraphFont || theme?.sansSerifFont];
  return Array.from(new Set(families.filter((family): family is string => Boolean(family))));
}
